import React from "react";
import Typography from "@mui/material/Typography";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { NumericFormat } from 'react-number-format';

interface INumericInputProps {
    label?: string;
    value: number;
    tooltip?: string;
    placeholder?: string;
    editable: boolean;
    readOnly: boolean;
    /**
     * When false, the numeric VALUE is locked (text field + steppers disabled) but the unit selector
     * stays active — used for "fixed" units (e.g. worktop Depth "auto", whose value is always 1) so the
     * user can still switch back to an editable unit. Defaults to following `editable`.
     */
    valueEditable?: boolean;
    /**
     * Ha meg van adva, a mezőben EZ a felirat látszik a szám helyett (a mező ilyenkor csak olvasható).
     * Olyan egységekhez, ahol a szám nem hordoz jelentést — pl. a munkalapok "auto"-ja: a felhasználó
     * "automatikus"-t lát, miközben a kimenő érték változatlan. A feliratot a DESCRIPTOR adja
     * (units.<egység>.valueLabel), nincs kódba írva.
     */
    valueText?: string;
    min: number;
    max: number;
    smallStep: number;
    largeStep: number;
    decimals?: number;
    unitLabel?: string;
    unitOptions?: string[];
    slotProps?: {
        textField?: any;
        buttonGroupButton?: any;
    };
    onValueChanged: (newValue: number) => void;
    onUnitChanged?: (newUnit: string) => void;
}

const CustomTextField = React.forwardRef((textFieldProps: any, ref) => (
    <TextField {...textFieldProps} ref={ref} />
));

const NumericInput: React.FC<INumericInputProps> = ({ ...props }) => {

    const value = React.useMemo(() => {
        return props.value.toFixed(props.decimals || 0);
    }, [props.value, props.decimals]);

    const [inputValue, setInputValue] = React.useState<string>(value);

    React.useEffect(() => {
        setInputValue(value);
    }, [value]);

    const clampValue = (v: number): number => {
        if (v > props.max) return props.max;
        if (v < props.min) return props.min;
        return v;
    };

    const safeParsed = (): number => {
        const parsed = parseFloat(inputValue.replace(",", "."));
        return !isNaN(parsed) ? parsed : props.min;
    };

    const increaseValue = (largeStep: boolean) => {
        const base = safeParsed();
        const step = largeStep ? props.largeStep : props.smallStep;
        const clamped = clampValue(base + step);
        props.onValueChanged(clamped);
        setInputValue(clamped.toFixed(props.decimals || 0));
    };

    const decreaseValue = (largeStep: boolean) => {
        const base = safeParsed();
        const step = largeStep ? props.largeStep : props.smallStep;
        const clamped = clampValue(base - step);
        props.onValueChanged(clamped);
        setInputValue(clamped.toFixed(props.decimals || 0));
    };

    return (
        <Stack direction="row" spacing={1} alignItems="stretch" width="100%">
            {props.readOnly ? (
                <Tooltip title={props.tooltip ?? ""} arrow>
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                        {props.valueText ?? `${props.value.toFixed(props.decimals || 0)} ${props.unitLabel || ""}`}
                    </Typography>
                </Tooltip>
            ) : (
                <Stack direction="row" spacing={0} alignItems="stretch" width="100%">
                    {props.valueText !== undefined ? (
                        <Tooltip title={props.tooltip ?? ""} arrow>
                            <TextField
                                label={props.label}
                                size="small"
                                fullWidth
                                value={props.valueText}
                                disabled
                                {...(props.slotProps?.textField || {})}
                                sx={{ ...props.slotProps?.textField?.sx }}
                            />
                        </Tooltip>
                    ) : (
                    <Tooltip title={props.tooltip ?? ""} arrow>
                        <NumericFormat
                            customInput={CustomTextField}
                            label={props.label}
                            size="small"
                            fullWidth
                            placeholder={props.placeholder ?? ""}
                            value={inputValue}
                            onValueChange={(v) => {
                                if (v.floatValue !== undefined) {
                                    setInputValue(v.floatValue.toString());
                                } else {
                                    setInputValue(v.value || "");
                                }
                            }}
                            onBlur={() => {
                                const parsed = safeParsed();
                                const clamped = clampValue(parsed);
                                // Csak TÉNYLEGES változásnál szólunk ki a szülőnek — különben a
                                // Kosárba gomb (mousedown→blur) változatlan értéknél is felesleges
                                // Unity-kört indítana, és a kosárba-tétel azt várná végig.
                                if (clamped !== props.value) {
                                    props.onValueChanged(clamped);
                                }
                                setInputValue(clamped.toFixed(props.decimals || 0));
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    increaseValue(e.ctrlKey);
                                } else if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    decreaseValue(e.ctrlKey); }
                                else if (e.key === "PageUp") { e.preventDefault(); increaseValue(true); }
                                else if (e.key === "PageDown") { e.preventDefault(); decreaseValue(true); }
                                else if (e.key === "Enter") {
                                    e.preventDefault();
                                    const parsed = safeParsed();
                                    const clamped = clampValue(parsed);
                                    if (clamped !== props.value) {
                                        props.onValueChanged(clamped);
                                    }
                                    setInputValue(clamped.toFixed(props.decimals || 0));
                                    (e.target as HTMLInputElement).blur();
                                }
                            }}
                            disabled={!props.editable || props.valueEditable === false}
                            decimalSeparator=","
                            decimalScale={props.decimals || 0}
                            fixedDecimalScale={true}
                            {...(props.slotProps?.textField || {})}
                            sx={{
                                ...props.slotProps?.textField?.sx
                            }}
                        />
                    </Tooltip>
                    )}

                    <ButtonGroup
                        disabled={!props.editable || props.valueEditable === false}
                        orientation="vertical"
                        size="small"
                        variant="text"
                        disableElevation
                        sx={{
                            ml: 1,
                            '& .MuiButton-root': { flex: 1 },
                            '& .MuiButtonGroup-grouped:not(:last-of-type)': { borderBottom: 'none' },
                        }}
                    >
                        <Button
                            tabIndex={-1}
                            size="small"
                            sx={{
                                padding: 0,
                                minWidth: '28px',
                                color: '#314c3d',
                                backgroundColor: 'transparent',
                                borderRadius: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(49, 76, 61, 0.08)',
                                },
                                ...props.slotProps?.buttonGroupButton,
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                increaseValue(e.ctrlKey);
                            }}
                        >
                            <KeyboardArrowUpIcon fontSize="small" />
                        </Button>
                        <Button
                            tabIndex={-1}
                            size="small"
                            sx={{
                                padding: 0,
                                minWidth: '28px',
                                color: '#314c3d',
                                backgroundColor: 'transparent',
                                borderRadius: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(49, 76, 61, 0.08)',
                                },
                                ...props.slotProps?.buttonGroupButton,
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                decreaseValue(e.ctrlKey);
                            }}
                        >
                            <KeyboardArrowDownIcon fontSize="small" />
                        </Button>
                    </ButtonGroup>

                    {props.unitOptions && props.unitOptions.length > 0 && (
                        <Stack sx={{ ml: 1 }}>
                            {props.unitOptions.length === 1 ? (
                                <Typography
                                    sx={{
                                        padding: "0 8px",
                                        border: "1px solid #c0c0c0",
                                        borderRadius: "4px",
                                        display: "flex",
                                        alignItems: "center",
                                        height: "100%",
                                        color: "#314c3d",
                                        backgroundColor: "#f6f6f6",
                                        minWidth: "40px",
                                        fontSize: "0.8rem",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {props.unitLabel}
                                </Typography>
                            ) : (
                                <Select
                                    disabled={!props.editable}
                                    size="small"
                                    value={props.unitLabel}
                                    onChange={(e) => props.onUnitChanged?.(e.target.value)}
                                    sx={{ height: "100%" }}
                                    variant="outlined"
                                >
                                    {props.unitOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </Select>
                            )}
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    );
};

export default NumericInput;